import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import clsx from "clsx";
import { Page } from "../../../components/Page";
import { TooltipLayer } from "./components/TooltipLayer";
import Spinner from "../../../components/Spinners";
import { BackBar } from "./components/BackBar";
import { Chip } from "./components/Chip";
import { KpiTile } from "./components/KpiTile";
import { OutcomeLegend, OutcomeSquare } from "./components/OutcomeSquare";
import { SectionCard } from "./components/SectionCard";
import { LoadError } from "./components/LoadError";
import { useRunRecord } from "./hooks/useTrackingHealth";
import { fmtActor, fmtDate } from "./format";
import {
  CHECK_OUTCOME_DISPLAY,
  POTENTIAL_FINDING_TAG,
  PROBLEM_SHORT_LABELS,
  VALIDATOR_LAYER_DISPLAY,
  VALIDATOR_LAYER_ORDER,
  checkLabel,
  humanizeEvidence,
  splitEvidence,
} from "./vocabulary";
import {
  hotelCopyContext,
  problemDescription,
  problemSolution,
} from "./problem-copy";
import type { HotelCopyContext } from "./problem-copy";
import type { CheckOutcome, ProblemId, RunRecord as RunRecordData } from "./types";

const ALL_PROBLEMS = Object.keys(PROBLEM_SHORT_LABELS) as ProblemId[];

function containerKey(problemId: string, route: string | null): string {
  return `${problemId}|${route ?? ""}`;
}

export function RunRecord() {
  const { runId = "" } = useParams<{ runId: string }>();
  const query = useRunRecord(runId);
  const run = query.data;
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  /**
   * What measured the hotel this run judged, so each container reads in that
   * hotel's words. `run.hotel` is null when the hotel row could not be read —
   * `hotelCopyContext` then answers the fleet-wide wording, which is the only
   * honest thing to print about a setup nobody could look up.
   */
  const copy = useMemo(() => hotelCopyContext(run?.hotel), [run]);

  // Tiles and matrix render from the ONE cells array (§6 rule 5).
  const outcomeCounts = useMemo(() => {
    const counts: Record<CheckOutcome, number> = {
      pass: 0,
      finding: 0,
      "not-checkable": 0,
      "not-applicable": 0,
      "not-run": 0,
    };
    for (const cell of run?.cells ?? []) counts[cell.outcome] += 1;
    return counts;
  }, [run]);

  // Columns: Website first, then routes in order of first appearance.
  const routeColumns = useMemo(() => {
    const routes: Array<string | null> = [null];
    for (const cell of run?.cells ?? []) {
      if (cell.route !== null && !routes.includes(cell.route)) {
        routes.push(cell.route);
      }
    }
    return routes;
  }, [run]);

  if (query.isLoading) {
    return (
      <Shell>
        <div className="flex justify-center py-16">
          <Spinner className="!text-brand-primary" />
        </div>
      </Shell>
    );
  }
  if (query.isError || !run) {
    return (
      <Shell>
        <LoadError
          error={query.error}
          label="this run"
          onRetry={() => query.refetch()}
        />
      </Shell>
    );
  }

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const skipped = run.runStatus === "skipped-validator-lock";

  return (
    <Shell>
      {/* Header */}
      <div className="mb-4">
        <BackBar
          crumbs={[
            { label: "Tracking Health", to: "/tracking/health" },
            {
              label: run.hotelName ?? run.customerId,
              to: `/tracking/health/hotels/${run.customerId}`,
            },
            { label: "Run record" },
          ]}
        />
        <h1 className="text-2xl font-semibold text-black mt-1">
          Validator run · {fmtDate(run.startedAt)}
        </h1>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <Chip tone="neutral">
            Trigger: {run.trigger}
            {run.trigger === "go-live" &&
              run.actor === "system:go-live-backstop" &&
              " (backstop)"}
          </Chip>
          <LayersChip run={run} />
          <Chip tone="neutral">actor {fmtActor(run.actor)}</Chip>
          <Chip tone="neutral">status {run.runStatus}</Chip>
        </div>
      </div>

      {skipped ? (
        <SectionCard title="Run skipped">
          <p className="text-sm text-content-secondary">
            This hotel was Validator-locked when the run was due — the lock is
            the reason, and no checks executed. There is no matrix to show:
            an empty one would read as sixteen clean problems.
          </p>
        </SectionCard>
      ) : (
        <>
          {/* Summary tiles — computed from the same cells array as the matrix. */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <KpiTile
              label="Pass"
              value={outcomeCounts.pass}
              numberClassName="text-success-dark"
            />
            <KpiTile
              label="Findings"
              value={outcomeCounts.finding}
              numberClassName="text-danger-dark"
            />
            <KpiTile
              label="Not-checkable"
              value={outcomeCounts["not-checkable"]}
              numberClassName="text-warning-dark"
            />
            <KpiTile
              label="Not-applicable"
              value={outcomeCounts["not-applicable"]}
              numberClassName="text-content-primary"
            />
            <KpiTile
              label="Not run"
              value={outcomeCounts["not-run"]}
              numberClassName="text-content-primary"
            />
          </div>

          {/* Outcome matrix */}
          <SectionCard title="Checks by problem">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-2 pr-3">
                      Problem
                    </th>
                    {routeColumns.map((route) => (
                      <th
                        key={route ?? "website"}
                        className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-2 pr-3 whitespace-nowrap"
                      >
                        {route ?? "Website"}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ALL_PROBLEMS.map((problemId) => (
                    <tr key={problemId} className="border-t border-[#E9E9EB]">
                      <td className="py-1.5 pr-3 text-xs text-content-secondary whitespace-nowrap">
                        P{problemId} {PROBLEM_SHORT_LABELS[problemId]}
                      </td>
                      {routeColumns.map((route) => {
                        const cells = run.cells.filter(
                          (cell) =>
                            cell.problemId === problemId && cell.route === route,
                        );
                        return (
                          <td
                            key={route ?? "website"}
                            className="py-1.5 pr-3 cursor-pointer"
                            onClick={() => toggle(containerKey(problemId, route))}
                          >
                            <span className="flex items-center gap-1 flex-wrap">
                              {cells.map((cell) => (
                                <OutcomeSquare
                                  key={cell.checkId}
                                  outcome={cell.outcome}
                                  title={`${cell.checkId} — ${CHECK_OUTCOME_DISPLAY[cell.outcome]}: ${cell.evidence}`}
                                />
                              ))}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 pt-3 border-t border-[#E9E9EB]">
              <OutcomeLegend />
            </div>
          </SectionCard>

          {/* Per-problem accordions */}
          <div className="flex flex-col gap-2 mt-4">
            {run.containers.map((container) => {
              const key = containerKey(container.problemId, container.route);
              const hasContent =
                container.findings.length > 0 ||
                container.potentialFindings.length > 0 ||
                container.contextNotes.length > 0 ||
                container.variantMatches.length > 0;
              const isOpen = expanded.has(key);
              const label = `P${container.problemId} — ${
                PROBLEM_SHORT_LABELS[container.problemId] ?? container.title
              }${container.route ? ` · ${container.route}` : ""}`;

              if (!hasContent && !isOpen) {
                return (
                  <button
                    key={key}
                    onClick={() => toggle(key)}
                    className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] px-4 py-2.5 text-left flex items-center gap-2"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#10B981] shrink-0" />
                    <span className="text-sm text-content-secondary">
                      {label} · {container.outcomes.length} checks · clean
                    </span>
                  </button>
                );
              }

              return (
                <div
                  key={key}
                  className="bg-white rounded-lg shadow-sm border border-[#E9E9EB]"
                >
                  <button
                    onClick={() => toggle(key)}
                    className="w-full px-4 py-2.5 text-left flex items-center justify-between gap-2"
                  >
                    <span className="text-sm font-medium text-content-primary">
                      {label}
                    </span>
                    <span className="text-xs text-gray-400">
                      {container.findings.length} finding
                      {container.findings.length === 1 ? "" : "s"} ·{" "}
                      {container.outcomes.length} checks {isOpen ? "▾" : "▸"}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 flex flex-col gap-3">
                      <ContainerBody container={container} hotel={copy} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Fingerprint footnotes — the derivation's inputs at run time. */}
          {run.fingerprintSnapshots.length > 0 && (
            <div className="mt-4 text-xs text-gray-400 flex flex-col gap-0.5">
              {run.fingerprintSnapshots.map((snapshot) => (
                <div key={snapshot.routeId}>
                  {snapshot.entryHop} — fingerprint read this run: written{" "}
                  {fmtDate(snapshot.writtenAt)} · depthLast{" "}
                  {snapshot.depthLastReached} · depthEver{" "}
                  {snapshot.depthEverReached}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Shell>
  );
}

// ─── Pieces ───────────────────────────────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <Page>
      <div className="p-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-neutral-200 flex-1">
        {children}
      </div>
      <TooltipLayer />
    </Page>
  );
}

/** The layers chip renders ALL planned layers, striking through the
 *  unexecuted (§5.4): a weekly sweep never plans `walk`, so it simply does
 *  not appear; a planned layer struck through means the substrate was
 *  unreachable and its cells read not-run below. */
function LayersChip({ run }: { run: RunRecordData }) {
  const planned = VALIDATOR_LAYER_ORDER.filter((layer) =>
    run.layersPlanned.includes(layer),
  );
  const executed = new Set(run.layersExecuted);
  return (
    <Chip tone="neutral">
      <span className="flex items-center gap-1">
        Layers:
        {planned.map((layer, i) => (
          <span key={layer} className="flex items-center gap-1">
            {i > 0 && <span className="text-gray-400">·</span>}
            <span
              className={clsx(
                !executed.has(layer) && "line-through text-gray-400",
              )}
              data-tip={
                executed.has(layer)
                  ? undefined
                  : "planned but not executed — the substrate was unreachable; every cell this layer owns reads not-run"
              }
            >
              {VALIDATOR_LAYER_DISPLAY[layer]}
            </span>
          </span>
        ))}
      </span>
    </Chip>
  );
}

function ContainerBody({
  container,
  hotel,
}: {
  container: RunRecordData["containers"][number];
  hotel: HotelCopyContext;
}) {
  const plain = problemDescription(container.problemId, hotel);

  return (
    <>
      {/* What this problem means on THIS hotel, in an operator's words — the
          findings below stay the measurement (composed in the portal, not the
          API). */}
      {plain && <p className="text-sm text-content-secondary">{plain}</p>}

      {container.findings.length > 0 && (
        <div className="px-3 py-2.5 rounded-md bg-danger-light/60 border border-danger-light">
          <div className="text-xs font-semibold text-danger-dark mb-1">
            Findings ({container.findings.length})
          </div>
          {/* Check named in English, ids and ISO windows humanized away, and
              the ratification tail dropped — same treatment as the problem
              card; the raw id and full sentence stay on hover. */}
          {container.findings.map((finding) => (
            <div
              key={finding.checkId}
              className="text-sm text-danger-dark py-0.5"
              data-tip={finding.evidence}
            >
              <span
                className="font-medium"
                data-tip={`Validator check ${finding.checkId}`}
              >
                {checkLabel(finding.checkId)}:
              </span>{" "}
              {splitEvidence(humanizeEvidence(finding.evidence)).head}
            </div>
          ))}
        </div>
      )}

      {container.potentialFindings.length > 0 && (
        <div className="px-3 py-2.5 rounded-md bg-warning-light/60 border border-warning-light">
          <div className="text-xs font-semibold text-warning-dark mb-1">
            Potential findings ({container.potentialFindings.length})
          </div>
          {container.potentialFindings.map((finding, i) => (
            <div key={`${finding.checkId}-${i}`} className="py-1">
              <div className="text-sm text-warning-dark">
                <span
                  className="font-medium"
                  data-tip={`Validator check ${finding.checkId}`}
                >
                  {checkLabel(finding.checkId)}
                </span>
                {" · "}
                {finding.source === "not-checkable"
                  ? "from a not-checkable check"
                  : finding.source === "stale-fingerprint"
                    ? "pass against a stale fingerprint"
                    : "pass against a shallow fingerprint"}
              </div>
              <div className="text-sm text-warning-dark">
                {humanizeEvidence(finding.evidence)}
              </div>
              {/* Every row in this list is readable-only by construction —
                  the tag renders unconditionally (§5.4). */}
              <span className="inline-flex items-center px-1.5 py-0.5 mt-0.5 rounded text-[10px] font-medium bg-[#F4F4F6] text-gray-500">
                {POTENTIAL_FINDING_TAG}
              </span>
            </div>
          ))}
        </div>
      )}

      {container.solutions.length > 0 && (
        <div className="text-sm">
          <div className="text-xs font-semibold text-content-secondary mb-1">
            Solution
          </div>
          {/* The operational instruction, in the operator's words — the raw
              catalog ref survives on hover for anyone tracing the strategy. */}
          {container.solutions.map((ref) => (
            <p
              key={ref}
              data-tip={ref}
              className="text-xs text-content-primary"
            >
              {problemSolution(container.problemId, hotel) ??
                `Catalog → P${container.problemId} fix strategy`}
            </p>
          ))}
        </div>
      )}

      {container.variantMatches.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-content-secondary mb-1">
            Potential solutions
          </div>
          {container.variantMatches
            .slice()
            .sort((a, b) => a.upstreamRank - b.upstreamRank)
            .map((variant) => (
              <div key={variant.ref} className="text-xs text-content-secondary py-0.5">
                Variant: {variant.ref} · applies_when matched · basis{" "}
                {variant.basis} · rank {variant.upstreamRank}
              </div>
            ))}
        </div>
      )}

      {container.contextNotes.length > 0 && (
        <div>
          {/* What the run deliberately did NOT conclude — the Inn at
              Richmond lesson: an invisible applicability decision reads as a
              silent skip (§5.4 list 5). Neutral, never a panel tint. */}
          <div className="text-xs font-semibold text-content-secondary mb-1">
            Context notes
          </div>
          {container.contextNotes.map((note, i) => (
            <div key={i} className="text-xs text-gray-500 py-0.5">
              {note}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
